import { isFullvisioSlotType } from '@omecare/utils';
import { useTranslation } from 'react-i18next';
import { SelectedDatesCard } from '@/components/sheets/slot-sheet/selected-dates-card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OfficeDoctorAttendingItem } from '@/hooks/queries/use-get-office';
import { DatePickerForm } from '../datepicker/datepicker-form';
import { RequiredLabel } from '../required-label';
import { useOpenOfficAgendaSlotFormViewModel } from './open-office-agenda-slot-form.viewmodel';
import { OpenOfficeAgendaSlotUseForm } from './use-open-office-agenda-slot-form';

type Props = {
  form: OpenOfficeAgendaSlotUseForm;
  doctorAttendings: OfficeDoctorAttendingItem[];
};

const OpenOfficeAgendaSlotForm = ({ form, doctorAttendings }: Props) => {
  const { t } = useTranslation();

  const viewModel = useOpenOfficAgendaSlotFormViewModel({
    form,
    doctorAttendings,
  });

  // Remote (fullvisio) slots are always "sans assistant·e", so the manual SA
  // toggle is forced on and disabled when Fullvisio is selected.
  const isFullvisio = isFullvisioSlotType(form.watch('slotType'));

  return (
    <Form {...form}>
      <form className="grid gap-5">
        {/* Doctor attending */}
        <FormField
          control={form.control}
          name="doctorAttendingId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <RequiredLabel>
                  {t('open-office-agenda-slot-form.doctor-attending.label')}
                </RequiredLabel>
              </FormLabel>

              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={t(
                        'open-office-agenda-slot-form.doctor-attending.placeholder',
                      )}
                    />
                  </SelectTrigger>
                </FormControl>

                <SelectContent side="bottom">
                  {viewModel.selectOptions.doctorAttendingOptions.length > 0 ? (
                    viewModel.selectOptions.doctorAttendingOptions.map(
                      (item) => (
                        <SelectItem
                          key={item.id}
                          value={item.value}
                        >
                          {t(item.label)}
                        </SelectItem>
                      ),
                    )
                  ) : (
                    <p className="px-2 py-6 text-center text-muted-foreground text-sm">
                      {t(
                        'open-office-agenda-slot-form.doctor-attending.list-empty',
                      )}
                    </p>
                  )}
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>
          )}
        />

        {/* Slot type */}
        <FormField
          control={form.control}
          name="slotType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('open-office-agenda-slot-form.slot-type.label')}
              </FormLabel>

              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent side="bottom">
                  {viewModel.selectOptions.agendaSlotTypeOptions.map((item) => (
                    <SelectItem
                      key={item.id}
                      value={item.value}
                    >
                      {t(item.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <FormDescription>
                {t('open-office-agenda-slot-form.slot-type.description')}
              </FormDescription>

              <FormMessage />
            </FormItem>
          )}
        />

        {/* Period of the day */}
        <FormField
          control={form.control}
          name="periodOfTheDay"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(
                  'open-office-agenda-slot-form.slot-recurrence-day-period.label',
                )}
              </FormLabel>

              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent side="bottom">
                  {viewModel.selectOptions.periodOfTheDayOptions.map((item) => (
                    <SelectItem
                      key={item.id}
                      value={item.id}
                    >
                      {t(item.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>
          )}
        />

        {/* Force without assistant */}
        <FormField
          control={form.control}
          name="forceWithoutAssistant"
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-row items-start gap-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value || isFullvisio}
                    disabled={isFullvisio}
                    onCheckedChange={(checked) =>
                      field.onChange(checked === true)
                    }
                  />
                </FormControl>

                <div className="grid gap-1 leading-none">
                  <FormLabel className="cursor-pointer font-normal">
                    {t(
                      'open-office-agenda-slot-form.force-without-assistant.label',
                    )}
                  </FormLabel>

                  <FormDescription>
                    {t(
                      'open-office-agenda-slot-form.force-without-assistant.description',
                    )}
                  </FormDescription>
                </div>
              </div>

              <FormMessage />
            </FormItem>
          )}
        />

        {/* Dates the slots will be opened on */}
        <DatePickerForm
          form={form}
          fieldName="specificDates"
          captionLayout="dropdown"
          inline={false}
          label={t('add-office-work-schedule-slot-form.specific-dates.label')}
          description={t(
            'add-office-work-schedule-slot-form.specific-dates.description',
          )}
          placeholder={t(
            'add-office-work-schedule-slot-form.specific-dates.placeholder',
          )}
          mode="multiple"
        />

        <SelectedDatesCard
          dates={viewModel.specificSlots.selectedDates}
          periodLabel={viewModel.specificSlots.periodSummaryLabel}
          onRemove={viewModel.api.removeSelectedDate}
          onClear={viewModel.api.clearSelectedDates}
        />
      </form>
    </Form>
  );
};

export default OpenOfficeAgendaSlotForm;
